// src/admin/AdminExploreCardsPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import WebExploreCardPreview from './WebExploreCardPreview';

const EXPLORE_CARDS_COLL = collection(db, 'exploreCards');

const BLANK_EXPLORE_CARD = {
  id: '', title: '', locationTag: '', startDate: '', endDate: '', imageUrl: '', galleryImageUrls: [], showGallery: true,
  isHidden: false,
  tags: [],
  assignedRoutes: [], // Routes where this card should appear (empty = all routes)
  visibilityType: 'all_passes', // 'no_pass', 'all_passes', or 'specific_routes'
  coverSections: [
    { id: 'default-title', type: 'title', field: 'title', content: '', enabled: true, 
      styling: { align: 'center', verticalAlign: 'bottom', size: 'medium', color: 'white', font: 'Barlow-Bold', fontWeight: 'bold', textShadow: true, opacity: 1, marginTop: 0, marginBottom: 5, marginLeft: 0, marginRight: 0 }, order: 0 },
    { id: 'default-location', type: 'location', field: 'locationTag', content: '', enabled: false,
      styling: { align: 'left', verticalAlign: 'top', size: 14, color: 'white', font: 'Caveat-Bold', backgroundColor: 'rgba(255,255,255,0.25)', padding: '4px 10px', borderRadius: 10, opacity: 1, fontWeight: 'normal', textShadow: false, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }, order: 1 },
    { id: 'default-dates', type: 'dateRange', field: 'dates', content: '', enabled: false,
      styling: { align: 'left', verticalAlign: 'bottom', size: 12, color: 'white', font: 'Caveat-Bold', fontWeight: '600', textShadow: true, opacity: 1, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 }, order: 2 },
  ],
  coverBackgroundStyle: { overlay: 'none', overlayColor: 'rgba(0,0,0,0.3)' },
  detailBackgroundColor: '#ffffff',
  mosaicPriority: 5,
  coverStyling: { titleAlign: 'center', titleSize: 'medium', titleColor: 'white', titleFont: 'Barlow-Bold', locationAlign: 'left', locationSize: 14, locationColor: 'white', locationFont: 'Caveat-Bold', dateAlign: 'left', dateSize: 12, dateColor: 'white', dateFont: 'Caveat-Bold', verticalAlign: 'bottom' },
  mosaicOptions: { allowedSizes: ['1x1', '1x2', '2x3'], priority: 5 },
  detailSections: [],
};


const BLANK_DETAIL_SECTION = {
  id: '', type: 'text', content: '',
  styling: { align: 'left', verticalAlign: 'top', size: 16, color: 'black', font: 'system', marginTop: 0, marginBottom: 20, marginLeft: 0, marginRight: 0, paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 },
  order: 0,
};


const getDefaultContentForType = (type) => {
  if (type === 'carousel') return [];
  if (type === 'map') return { latitude: '', longitude: '' };
  return '';
};

function sanitizeFilename(filename) {
  return filename.replace(/[^a-zA-Z0-9.-]/g, '_').replace(/_{2,}/g, '_').replace(/^_|_$/g, '');
}

function generateTempId() { return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`; }

// Color Picker Component
const ColorPicker = ({ value, onChange, label, placeholder = '#ffffff' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColor, setTempColor] = useState(value || '#ffffff');
  const pickerRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  const presetColors = [
    '#ffffff', '#000000', '#ff0000', '#00ff00', '#0000ff', '#ffff00',
    '#ff00ff', '#00ffff', '#ff8800', '#8800ff', '#00ff88', '#ff0088',
    '#f0f0f0', '#d0d0d0', '#a0a0a0', '#808080', '#606060', '#404040',
    '#ffd700', '#ff69b4', '#4169e1', '#32cd32', '#ff4500', '#9370db'
  ];
  
  return (
    <div style={{ position: 'relative' }} ref={pickerRef}>
      {label && <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>{label}</label>}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div 
          onClick={() => setIsOpen(!isOpen)}
          style={{
            width: '40px',
            height: '40px',
            backgroundColor: value || '#ffffff',
            border: '2px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            position: 'relative',
            boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)'
          }}
        >
          {!value && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '20px',
              color: '#999'
            }}>+</div>
          )}
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1,
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <input
          type="color"
          value={value || '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          style={{
            width: '40px',
            height: '40px',
            padding: '0',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        />
      </div>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '0',
          marginTop: '8px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1000,
          minWidth: '240px'
        }}>
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px' }}>Preset Colors:</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '6px' }}>
              {presetColors.map(color => (
                <div
                  key={color}
                  onClick={() => {
                    onChange(color);
                    setIsOpen(false);
                  }}
                  style={{
                    width: '30px',
                    height: '30px',
                    backgroundColor: color,
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
              ))}
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <input
              type="color"
              value={tempColor}
              onChange={(e) => {
                setTempColor(e.target.value);
                onChange(e.target.value);
              }}
              style={{
                width: '100%',
                height: '40px',
                padding: '0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            />
          </div>
          
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              style={{
                padding: '6px 12px',
                backgroundColor: '#f5f5f5',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              style={{
                padding: '6px 12px',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const convertImageToWebP = async (file, maxWidth = 2000, maxHeight = 2000, quality = 1.0) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width *= ratio; height *= ratio;
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try WebP first
        canvas.toBlob((blob) => {
          if (blob) {
            const webpFileName = file.name.replace(/\.[^/.]+$/, '') + '.webp';
            const webpFile = new File([blob], webpFileName, { type: 'image/webp' });
            resolve(webpFile);
          } else {
            // Fallback to PNG if WebP not supported
            canvas.toBlob((pngBlob) => {
              if (pngBlob) {
                const pngFileName = file.name.replace(/\.[^/.]+$/, '') + '.png';
                const pngFile = new File([pngBlob], pngFileName, { type: 'image/png' });
                resolve(pngFile);
              } else {
                reject(new Error('Failed to convert image'));
              }
            }, 'image/png', 1.0);
          }
        }, 'image/webp', quality);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

function AdminExploreCardsPage() {
  // Add viewport meta for better mobile experience
  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.content = "width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes";
    }
    return () => {
      if (viewport) {
        viewport.content = "width=device-width, initial-scale=1.0";
      }
    };
  }, []);

  const [cardsList, setCardsList] = useState([]);
  const [filteredCardsList, setFilteredCardsList] = useState([]);
  const [selectedCard, setSelectedCard] = useState(BLANK_EXPLORE_CARD);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [galleryImageFiles, setGalleryImageFiles] = useState([]);
  const [galleryImagePreviews, setGalleryImagePreviews] = useState([]);
  const [tempCardId, setTempCardId] = useState(null);
  const [detailImageFiles, setDetailImageFiles] = useState({});
  const [detailImagePreviews, setDetailImagePreviews] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [galleryUploadProgress, setGalleryUploadProgress] = useState({});
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('cover');
  const [isConverting, setIsConverting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [allExistingTags, setAllExistingTags] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [routesList, setRoutesList] = useState([]); // Added routes list

const fetchExploreCards = useCallback(async () => {
  setIsLoading(true);
  try {
    const [cardsSnapshot, routesSnapshot] = await Promise.all([
      getDocs(EXPLORE_CARDS_COLL),
      getDocs(collection(db, 'routes'))
    ]);
    
    // Process routes
    const routes = routesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setRoutesList(routes);
    
    const snapshot = cardsSnapshot;
    const fetchedCards = snapshot.docs.map((d) => {
      const data = d.data();
      return {
        ...BLANK_EXPLORE_CARD, ...data, id: d.id,
        isHidden: data.isHidden || false,
        tags: data.tags || [],
        coverSections: (data.coverSections || BLANK_EXPLORE_CARD.coverSections).map((section, index) => {
          const defaultSection = BLANK_EXPLORE_CARD.coverSections.find(s => s.id === section.id) || BLANK_EXPLORE_CARD.coverSections[index];
          return {
            ...section,
            content: section.content || '',
            styling: {
              ...(defaultSection ? defaultSection.styling : {}),
              ...section.styling,
              // Ensure font is always present
              font: section.styling?.font || defaultSection?.styling?.font || 'Caveat-Bold'
            }
          };
        }),
        coverBackgroundStyle: { ...BLANK_EXPLORE_CARD.coverBackgroundStyle, ...(data.coverBackgroundStyle || {}) },
        mosaicPriority: data.mosaicPriority !== undefined ? data.mosaicPriority : (data.mosaicOptions?.priority || 5),
        coverStyling: { ...BLANK_EXPLORE_CARD.coverStyling, ...(data.coverStyling || {}) },
        mosaicOptions: { ...BLANK_EXPLORE_CARD.mosaicOptions, ...(data.mosaicOptions || {}) },
        detailSections: (data.detailSections || []).map(section => ({
          ...section, content: section.content !== undefined ? section.content : getDefaultContentForType(section.type),
          styling: { ...BLANK_DETAIL_SECTION.styling, ...(section.styling || {}) }
        }))
      };
    }).sort((a, b) => (a.mosaicPriority || 5) - (b.mosaicPriority || 5));
    setCardsList(fetchedCards);
    setFilteredCardsList(fetchedCards);
    setError('');
  } catch (err) {
    console.error("Error fetching explore cards:", err);
    setError('Failed to fetch explore cards. Please try again.');
  } finally { setIsLoading(false); }
}, []);

  useEffect(() => { fetchExploreCards(); }, [fetchExploreCards]);

  // Fetch all existing tags for autocomplete
  useEffect(() => {
    const fetchAllTags = async () => {
      try {
        const snapshot = await getDocs(EXPLORE_CARDS_COLL);
        const allTags = new Set();
        snapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.tags && Array.isArray(data.tags)) {
            data.tags.forEach(tag => allTags.add(tag));
          }
        });
        setAllExistingTags(Array.from(allTags).sort());
      } catch (err) {
        console.error("Error fetching tags:", err);
      }
    };
    fetchAllTags();
  }, [cardsList]);

  // Search functionality
  useEffect(() => {
    let dataToFilter = [...cardsList];
    
    if (searchText.trim() !== '') {
      const lowercasedSearchText = searchText.toLowerCase();
      const searchTerms = lowercasedSearchText.split(/\s+/).filter(term => term.length > 0);
      
      // Score each card based on matches
      const scoredCards = dataToFilter.map(item => {
        let score = 0;
        let matchedTerms = 0;
        
        searchTerms.forEach(term => {
          // Title matches (highest priority - 10 points)
          if (item.title && item.title.toLowerCase().includes(term)) {
            score += 10;
            matchedTerms++;
          }
          
          // Tag matches (high priority - 8 points)
          if (item.tags && Array.isArray(item.tags)) {
            item.tags.forEach(tag => {
              if (tag.toLowerCase().includes(term)) {
                score += 8;
                matchedTerms++;
              }
            });
          }
          
          // Location matches (medium priority - 5 points)
          if (item.locationTag && item.locationTag.toLowerCase().includes(term)) {
            score += 5;
            matchedTerms++;
          }
          
          // Description matches (low priority - 2 points)
          if (item.description && item.description.toLowerCase().includes(term)) {
            score += 2;
            matchedTerms++;
          }
          
          // Detail sections matches (low priority - 1 point)
          if (item.detailSections && Array.isArray(item.detailSections)) {
            item.detailSections.forEach(section => {
              if (section.content && typeof section.content === 'string' && section.content.toLowerCase().includes(term)) {
                score += 1;
                matchedTerms++;
              }
            });
          }
        });
        
        return {
          ...item,
          searchScore: score,
          matchedTerms: matchedTerms
        };
      });
      
      // Filter to only include cards that matched at least one term
      dataToFilter = scoredCards
        .filter(item => item.matchedTerms > 0)
        .sort((a, b) => {
          // First sort by search score (descending)
          if (b.searchScore !== a.searchScore) {
            return b.searchScore - a.searchScore;
          }
          // Then by mosaic priority (descending)
          return (b.mosaicPriority || 5) - (a.mosaicPriority || 5);
        });
    }
    
    setFilteredCardsList(dataToFilter);
  }, [searchText, cardsList]);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setSelectedCard((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  }, []);

  // Tag-related functions
  const formatTag = (tag) => {
    return tag.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const handleAddTag = () => {
    const formattedTag = formatTag(tagInput);
    if (formattedTag && !selectedCard.tags.includes(formattedTag)) {
      if (selectedCard.tags.length >= 13) {
        setError('Maximum 13 tags allowed per card');
        return;
      }
      setSelectedCard(prev => ({ ...prev, tags: [...prev.tags, formattedTag] }));
      setTagInput('');
      setError('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setSelectedCard(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAllowedSizeToggle = (size) => {
    setSelectedCard(prev => {
      const currentSizes = prev.mosaicOptions.allowedSizes || [];
      const newSizes = currentSizes.includes(size) ? currentSizes.filter(s => s !== size) : [...currentSizes, size];
      if (newSizes.length === 0) { setError('At least one mosaic size must be selected'); return prev; }
      return { ...prev, mosaicOptions: { ...prev.mosaicOptions, allowedSizes: newSizes } };
    });
    setError('');
  };

  const addCoverSection = () => {
    const newSection = { id: generateTempId(), type: 'customText', content: '', enabled: true,
      styling: { align: 'center', verticalAlign: 'middle', size: 'medium', color: 'white', fontWeight: 'normal', textShadow: false, opacity: 1, marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 },
      order: selectedCard.coverSections.length };
    setSelectedCard(prev => ({ ...prev, coverSections: [...prev.coverSections, newSection] }));
  };

  const updateCoverSection = (sectionId, field, value) => {
    setSelectedCard(prev => ({
      ...prev, coverSections: prev.coverSections.map(section => section.id === sectionId ? { ...section, [field]: value } : section)
    }));
  };

  const updateCoverSectionStyling = (sectionId, styleField, value) => {
    setSelectedCard(prev => ({
      ...prev, coverSections: prev.coverSections.map(section => section.id === sectionId
        ? { ...section, styling: { ...section.styling, [styleField]: value } } : section)
    }));
  };

  const removeCoverSection = (sectionId) => {
    const section = selectedCard.coverSections.find(s => s.id === sectionId);
    if (section && section.id.startsWith('default-')) { setError('Default cover elements cannot be removed, but can be disabled'); return; }
    setSelectedCard(prev => ({ ...prev, coverSections: prev.coverSections.filter(section => section.id !== sectionId) }));
  };

  const moveCoverSection = (sectionId, direction) => {
    setSelectedCard(prev => {
      const sections = [...prev.coverSections];
      const index = sections.findIndex(s => s.id === sectionId);
      if (direction === 'up' && index > 0) { [sections[index], sections[index - 1]] = [sections[index - 1], sections[index]]; }
      else if (direction === 'down' && index < sections.length - 1) { [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]]; }
      return { ...prev, coverSections: sections.map((s, i) => ({ ...s, order: i })) };
    });
  };

  const addDetailSection = () => {
    const newSection = { ...BLANK_DETAIL_SECTION, id: generateTempId(), order: selectedCard.detailSections.length, styling: { ...BLANK_DETAIL_SECTION.styling } };
    setSelectedCard(prev => ({ ...prev, detailSections: [...prev.detailSections, newSection] }));
  };

  const updateDetailSection = (sectionId, field, value) => {
    setSelectedCard(prev => ({
      ...prev, detailSections: prev.detailSections.map(section => {
        if (section.id === sectionId) {
          const updates = { [field]: value };
          if (field === 'type' && value !== section.type) { updates.content = getDefaultContentForType(value); }
          return { ...section, ...updates, styling: section.styling || { ...BLANK_DETAIL_SECTION.styling } };
        }
        return section;
      })
    }));
  };

  const updateDetailSectionStyling = (sectionId, styleField, value) => {
    setSelectedCard(prev => ({
      ...prev, detailSections: prev.detailSections.map(section => section.id === sectionId
        ? { ...section, styling: { ...section.styling, [styleField]: value } } : section)
    }));
  };

  const removeDetailSection = (sectionId) => {
    setSelectedCard(prev => ({ ...prev, detailSections: prev.detailSections.filter(section => section.id !== sectionId) }));
    const newDetailImageFiles = { ...detailImageFiles };
    const newDetailImagePreviews = { ...detailImagePreviews };
    delete newDetailImageFiles[sectionId]; delete newDetailImagePreviews[sectionId];
    setDetailImageFiles(newDetailImageFiles); setDetailImagePreviews(newDetailImagePreviews);
  };

  const moveDetailSection = (sectionId, direction) => {
    setSelectedCard(prev => {
      const sections = [...prev.detailSections];
      const index = sections.findIndex(s => s.id === sectionId);
      if (direction === 'up' && index > 0) { [sections[index], sections[index - 1]] = [sections[index - 1], sections[index]]; }
      else if (direction === 'down' && index < sections.length - 1) { [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]]; }
      return { ...prev, detailSections: sections.map((s, i) => ({ ...s, order: i })) };
    });
  };

  const handleDetailImageChange = async (sectionId, files) => {
    if (files.length > 0) {
      const file = files[0];
      if (!file.type.startsWith('image/')) { setError('Please select a valid image file.'); return; }
      if (file.size > 5 * 1024 * 1024) { setError('Image size must be less than 5MB.'); return; }
      try {
        setIsConverting(true);
        const webpFile = await convertImageToWebP(file);
        setDetailImageFiles(prev => ({ ...prev, [sectionId]: webpFile }));
        setDetailImagePreviews(prev => ({ ...prev, [sectionId]: URL.createObjectURL(webpFile) }));
        setError('');
      } catch (err) {
        setError('Failed to process image. Please try another file.');
        console.error('Image conversion error:', err);
      } finally { setIsConverting(false); }
    }
  };

  const handleCoverImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) { setError('Please select a valid image file.'); return; }
      if (file.size > 5 * 1024 * 1024) { setError('Image size must be less than 5MB.'); return; }
      try {
        setIsConverting(true);
        const webpFile = await convertImageToWebP(file);
        setCoverImageFile(webpFile); setCoverImagePreview(URL.createObjectURL(webpFile)); setError('');
      } catch (err) { setError('Failed to process image. Please try another file.'); console.error('Image conversion error:', err); }
      finally { setIsConverting(false); }
    } else { setCoverImageFile(null); setCoverImagePreview(selectedCard.id ? selectedCard.imageUrl : ''); }
  };

  const handleGalleryImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const invalidFiles = files.filter(file => !file.type.startsWith('image/'));
      if (invalidFiles.length > 0) { setError('All files must be valid images.'); return; }
      const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
      if (oversizedFiles.length > 0) { setError('All images must be less than 5MB.'); return; }
      try {
        setIsConverting(true);
        const webpFiles = await Promise.all(files.map(file => convertImageToWebP(file)));
        setGalleryImageFiles(webpFiles);
        const newPreviews = webpFiles.map(file => URL.createObjectURL(file));
        setGalleryImagePreviews(newPreviews); setError('');
      } catch (err) { setError('Failed to process images. Please try again.'); console.error('Image conversion error:', err); }
      finally { setIsConverting(false); }
    } else { setGalleryImageFiles([]); setGalleryImagePreviews([]); }
  };

  const handleAddNew = () => {
    if (!window.confirm('Are you sure you want to clear the form? Any unsaved changes will be lost.')) return;
    setSelectedCard(BLANK_EXPLORE_CARD); setCoverImageFile(null); setCoverImagePreview('');
    setGalleryImageFiles([]); setGalleryImagePreviews([]); setDetailImageFiles({}); setDetailImagePreviews({});
    setGalleryUploadProgress({}); setTempCardId(null); setError(''); setSuccessMessage(''); setActiveTab('cover');
    setTagInput('');
    const coverFileInput = document.getElementById('exploreCardImageFile');
    if (coverFileInput) coverFileInput.value = null;
    const galleryFileInput = document.getElementById('exploreCardGalleryFiles');
    if (galleryFileInput) galleryFileInput.value = null;
  };

  const handleEdit = (card) => {
    const cardWithDefaults = { 
      ...BLANK_EXPLORE_CARD, ...card,
      isHidden: card.isHidden || false,
      tags: card.tags || [],
      coverSections: (card.coverSections || BLANK_EXPLORE_CARD.coverSections).map(section => ({ ...section, content: section.content || '' })),
      coverBackgroundStyle: { ...BLANK_EXPLORE_CARD.coverBackgroundStyle, ...(card.coverBackgroundStyle || {}) },
      mosaicPriority: card.mosaicPriority !== undefined ? card.mosaicPriority : (card.mosaicOptions?.priority || 5),
      coverStyling: { ...BLANK_EXPLORE_CARD.coverStyling, ...(card.coverStyling || {}) },
      mosaicOptions: { ...BLANK_EXPLORE_CARD.mosaicOptions, ...(card.mosaicOptions || {}) },
      detailSections: (card.detailSections || []).map(section => ({
        ...section, content: section.content !== undefined ? section.content : getDefaultContentForType(section.type),
        styling: { ...BLANK_DETAIL_SECTION.styling, ...(section.styling || {}) }
      }))
    };
    setSelectedCard(cardWithDefaults); setCoverImageFile(null); setCoverImagePreview(cardWithDefaults.imageUrl || '');
    setGalleryImageFiles([]); setGalleryImagePreviews([]); setDetailImageFiles({}); setDetailImagePreviews({});
    setGalleryUploadProgress({}); setTempCardId(null); setError(''); setSuccessMessage(''); setActiveTab('cover');
    setTagInput('');
    window.scrollTo(0, 0);
  };

  const handleDeleteGalleryImage = async (imageUrlToDelete, cardId) => {
    if (!window.confirm('Are you sure you want to delete this gallery image?')) return;
    setIsSubmitting(true); setError('');
    try {
      const updatedGalleryUrls = selectedCard.galleryImageUrls.filter(url => url !== imageUrlToDelete);
      const cardRef = doc(db, 'exploreCards', cardId);
      await updateDoc(cardRef, { galleryImageUrls: updatedGalleryUrls });
      try { const imageRef = ref(storage, imageUrlToDelete); await deleteObject(imageRef); }
      catch (storageErr) { console.warn("Failed to delete image from storage:", storageErr); }
      setSelectedCard(prev => ({ ...prev, galleryImageUrls: updatedGalleryUrls }));
      setCardsList(prevList => prevList.map(c => c.id === cardId ? { ...c, galleryImageUrls: updatedGalleryUrls } : c));
      setSuccessMessage('Gallery image deleted successfully.');
    } catch (err) { console.error("Error deleting gallery image:", err); setError(`Failed to delete gallery image: ${err.message}`); }
    finally { setIsSubmitting(false); }
  };

  const handleDeleteCard = async (cardId, imageUrlToDelete, galleryUrlsToDelete) => {
    if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
      console.error("handleDeleteCard: Invalid or missing cardId provided.", cardId);
      setError("Cannot delete card: Invalid card identifier. Please refresh and try again."); return;
    }
    if (!window.confirm('Are you sure you want to delete this explore card? This cannot be undone.')) return;
    setIsSubmitting(true); setError(''); setSuccessMessage('');
    try {
      await deleteDoc(doc(db, 'exploreCards', cardId));
      const imagesToDelete = [...(imageUrlToDelete ? [imageUrlToDelete] : []), ...(galleryUrlsToDelete || [])];
      const deletePromises = imagesToDelete.map(url => deleteObject(ref(storage, url)).catch(err => console.warn(`Failed to delete image ${url}:`, err)));
      await Promise.all(deletePromises);
      setSuccessMessage('Explore card deleted successfully.');
      setCardsList((prev) => prev.filter((card) => card.id !== cardId));
      if (selectedCard.id === cardId) { handleAddNew(); }
    } catch (err) { console.error("Error deleting explore card:", err); setError(`Failed to delete explore card: ${err.message}`); }
    finally { setIsSubmitting(false); }
  };

  const handleToggleHide = async (cardId, currentHiddenState) => {
    if (!cardId || typeof cardId !== 'string' || cardId.trim() === '') {
      console.error("handleToggleHide: Invalid or missing cardId provided.", cardId);
      setError("Cannot toggle card visibility: Invalid card identifier. Please refresh and try again."); return;
    }
    setIsSubmitting(true); setError('');
    try {
      const cardRef = doc(db, 'exploreCards', cardId);
      await updateDoc(cardRef, { isHidden: !currentHiddenState });
      setCardsList(prevList => prevList.map(c => c.id === cardId ? { ...c, isHidden: !currentHiddenState } : c));
      if (selectedCard.id === cardId) {
        setSelectedCard(prev => ({ ...prev, isHidden: !currentHiddenState }));
      }
      setSuccessMessage(`Card ${!currentHiddenState ? 'hidden' : 'shown'} successfully.`);
    } catch (err) { console.error("Error toggling card visibility:", err); setError(`Failed to toggle card visibility: ${err.message}`); }
    finally { setIsSubmitting(false); }
  };

  const uploadFile = (file, path, progressCallback) => {
    return new Promise((resolve, reject) => {
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed',
        (snapshot) => progressCallback((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
        (error) => reject(error),
        async () => { const downloadURL = await getDownloadURL(uploadTask.snapshot.ref); resolve(downloadURL); }
      );
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (selectedCard.mosaicOptions.allowedSizes.length === 0) { setError('At least one mosaic size must be selected.'); return; }
    const hasEnabledTitle = selectedCard.coverSections.some(section => section.field === 'title' && section.enabled && selectedCard.title);
    if (!hasEnabledTitle) { setError('Title is required and must be enabled in Cover Elements.'); return; }
    const hasEnabledDates = selectedCard.coverSections.some(section => section.field === 'dates' && section.enabled);
    if (hasEnabledDates && (!selectedCard.startDate || !selectedCard.endDate)) { setError('Start Date and End Date are required if date element is enabled.'); return; }
    if (hasEnabledDates && new Date(selectedCard.endDate) < new Date(selectedCard.startDate)) { setError('End date cannot be before start date.'); return; }

    setIsSubmitting(true); setUploadProgress(0); setGalleryUploadProgress({}); setError(''); setSuccessMessage('');
    let cardDataToSave = { ...selectedCard };
    delete cardDataToSave.id;
    const cardIdForStorage = selectedCard.id || tempCardId || generateTempId();
    if (!selectedCard.id && !tempCardId) { setTempCardId(cardIdForStorage); }

    try {
      let finalCoverImageUrl = selectedCard.imageUrl;
      if (coverImageFile) {
        const sanitizedFilename = sanitizeFilename(coverImageFile.name);
        const timestamp = Date.now();
        const uniqueId = Math.random().toString(36).substr(2, 9);
        const coverImagePath = `exploreCardImages/cover/${timestamp}_${uniqueId}_${sanitizedFilename}`;
        const oldCoverImageUrl = selectedCard.id ? selectedCard.imageUrl : null;
        finalCoverImageUrl = await uploadFile(coverImageFile, coverImagePath, setUploadProgress);
        if (oldCoverImageUrl && oldCoverImageUrl !== finalCoverImageUrl) {
          try { await deleteObject(ref(storage, oldCoverImageUrl)); } catch (delErr) { console.warn("Failed to delete old cover image:", delErr); }
        }
      }
      cardDataToSave.imageUrl = finalCoverImageUrl;

      let newGalleryUrls = [];
      if (galleryImageFiles.length > 0) {
        for (let i = 0; i < galleryImageFiles.length; i++) {
          const file = galleryImageFiles[i];
          const sanitizedFilename = sanitizeFilename(file.name);
          const timestamp = Date.now();
          const uniqueId = Math.random().toString(36).substr(2, 9);
          const galleryImagePath = `exploreCardImages/gallery/${cardIdForStorage}/${timestamp}_${uniqueId}_${sanitizedFilename}`;
          const progressSetter = (progress) => setGalleryUploadProgress(prev => ({ ...prev, [file.name]: progress }));
          const url = await uploadFile(file, galleryImagePath, progressSetter);
          newGalleryUrls.push(url);
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      cardDataToSave.galleryImageUrls = [...(selectedCard.galleryImageUrls || []), ...newGalleryUrls];

      for (const section of cardDataToSave.detailSections) {
        if ((section.type === 'image' || section.type === 'backgroundImage') && detailImageFiles[section.id]) {
          const file = detailImageFiles[section.id];
          const sanitizedFilename = sanitizeFilename(file.name);
          const timestamp = Date.now();
          const uniqueId = Math.random().toString(36).substr(2, 9);
          const detailImagePath = `exploreCardImages/details/${cardIdForStorage}/${timestamp}_${uniqueId}_${sanitizedFilename}`;
          const url = await uploadFile(file, detailImagePath, (progress) => { console.log(`Uploading detail image: ${progress}%`); });
          section.content = url;
        }
      }

      let savedCardId = selectedCard.id;
      if (selectedCard.id) {
        cardDataToSave.updatedAt = serverTimestamp();
        await updateDoc(doc(db, 'exploreCards', selectedCard.id), cardDataToSave);
        setSuccessMessage('Explore card updated successfully!');
      } else {
        cardDataToSave.createdAt = serverTimestamp();
        cardDataToSave.updatedAt = serverTimestamp();
        const docRef = await addDoc(EXPLORE_CARDS_COLL, cardDataToSave);
        savedCardId = docRef.id;
        cardDataToSave.id = docRef.id;
        setSuccessMessage('Explore card added successfully!');
      }

      await fetchExploreCards();
      setSelectedCard({ ...BLANK_EXPLORE_CARD, ...cardDataToSave, id: savedCardId });
      setCoverImageFile(null); setGalleryImageFiles([]); setGalleryImagePreviews([]);
      setDetailImageFiles({}); setDetailImagePreviews({}); setTempCardId(null);
      const coverFileInput = document.getElementById('exploreCardImageFile');
      if (coverFileInput) coverFileInput.value = null;
      const galleryFileInput = document.getElementById('exploreCardGalleryFiles');
      if (galleryFileInput) galleryFileInput.value = null;
    } catch (err) { console.error("Error saving explore card:", err); setError(`Failed to save explore card: ${err.message}`); }
    finally { setIsSubmitting(false); }
  };

  const CoverPreviewWithSizeSelector = ({ selectedCard, coverImagePreview }) => {
    const [previewSize, setPreviewSize] = useState('2x3');
    const sizeStyles = { '1x1': { width: '100px', height: '100px' }, '1x2': { width: '100px', height: '216px' }, '2x3': { width: '216px', height: '332px' } };
    const previewContainerStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' };
    const sizeSelectorStyle = { display: 'flex', gap: '10px', marginBottom: '20px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '8px', width: '100%', justifyContent: 'center', flexWrap: 'wrap' };
    const sizeButtonStyle = (size) => ({ padding: '8px 16px', border: '2px solid #ccc', borderRadius: '6px', cursor: 'pointer', backgroundColor: previewSize === size ? '#007bff' : 'white', color: previewSize === size ? 'white' : '#333', fontWeight: previewSize === size ? 'bold' : 'normal', transition: 'all 0.2s', fontSize: '14px' });
    const previewAreaStyle = { width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '380px', position: 'relative' };
    const previewWrapperStyle = { ...sizeStyles[previewSize], border: '2px dashed #ccc', borderRadius: '15px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#fafafa', transition: 'all 0.3s ease', position: 'relative', overflow: 'hidden' };
    const scaleFactors = { '1x1': 0.286, '1x2': 0.286, '2x3': 0.617 };
    const scaledCardStyle = { transform: `scale(${scaleFactors[previewSize]})`, transformOrigin: 'center center', transition: 'transform 0.3s ease', pointerEvents: 'none' };
    const sizeLabelStyle = { fontSize: '12px', color: '#666', textAlign: 'center', marginTop: '20px' };
    const dimensionsLabelStyle = { position: 'absolute', top: '-25px', left: '50%', transform: 'translateX(-50%)', fontSize: '11px', color: '#999', backgroundColor: 'white', padding: '2px 8px', borderRadius: '3px', border: '1px solid #ddd' };
    
    return (
      <div style={previewContainerStyle}>
        <div style={sizeSelectorStyle}>
          <div style={{ fontSize: '14px', fontWeight: 'bold', width: '100%', textAlign: 'center', marginBottom: '10px' }}>Preview Size:</div>
          {['1x1', '1x2', '2x3'].map(size => (
            <button key={size} type="button" style={sizeButtonStyle(size)} onClick={() => setPreviewSize(size)}
              onMouseOver={(e) => { if (previewSize !== size) { e.target.style.backgroundColor = '#e3f2fd'; e.target.style.borderColor = '#007bff'; } }}
              onMouseOut={(e) => { if (previewSize !== size) { e.target.style.backgroundColor = 'white'; e.target.style.borderColor = '#ccc'; } }}>
              {size === '1x1' ? 'Square (1×1)' : size === '1x2' ? 'Tall (1×2)' : 'Large (2×3)'}
            </button>
          ))}
        </div>
        <div style={previewAreaStyle}>
          <div style={previewWrapperStyle}>
            <div style={dimensionsLabelStyle}>{sizeStyles[previewSize].width} × {sizeStyles[previewSize].height}</div>
            <div style={scaledCardStyle}>
              <WebExploreCardPreview title={selectedCard.title} locationTag={selectedCard.locationTag} startDate={selectedCard.startDate} endDate={selectedCard.endDate} imageUrl={coverImagePreview || selectedCard.imageUrl} coverStyling={selectedCard.coverStyling} coverSections={selectedCard.coverSections} coverBackgroundStyle={selectedCard.coverBackgroundStyle} showCoverOnly={true} />
            </div>
          </div>
        </div>
        <div style={sizeLabelStyle}>This preview shows how your card's cover will appear at {previewSize} size in the mosaic grid.</div>
        {selectedCard.mosaicOptions && selectedCard.mosaicOptions.allowedSizes && !selectedCard.mosaicOptions.allowedSizes.includes(previewSize) && (
          <div style={{ fontSize: '12px', color: '#ff6b6b', marginTop: '10px', padding: '8px 12px', backgroundColor: '#ffe0e0', borderRadius: '4px', textAlign: 'center', border: '1px solid #ffcccc' }}>
            ⚠️ Note: {previewSize} is not currently enabled in the allowed sizes for this card
          </div>
        )}
      </div>
    );
  };

const DetailPreview = ({ selectedCard, coverImagePreview, galleryImagePreviews, detailImagePreviews }) => {
    const previewContainerStyle = { 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        width: '100%', 
        marginTop: '20px',
        overflow: 'visible'  // Ensure content isn't clipped
    };
    
    const previewWrapperStyle = { 
        width: '400px',  // Increased width to accommodate full card plus margins
        height: '450px',  // Increased height for better visibility
        padding: '25px',  // More padding to show full card without clipping
        backgroundColor: '#f5f5f5',  // Light background to see card edges
        borderRadius: '12px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible'  // Prevent any clipping
    };
    
    const sizeLabelStyle = { 
        fontSize: '12px', 
        color: '#666', 
        textAlign: 'center', 
        marginTop: '20px',
        maxWidth: '350px' // Match card width
    };
    
    return (
        <div style={previewContainerStyle}>
            <div style={previewWrapperStyle}>
                <WebExploreCardPreview 
                    title={selectedCard.title} 
                    locationTag={selectedCard.locationTag} 
                    startDate={selectedCard.startDate} 
                    endDate={selectedCard.endDate} 
                    description={selectedCard.description} 
                    imageUrl={coverImagePreview || selectedCard.imageUrl} 
                    galleryImageUrls={galleryImagePreviews.length > 0 ? galleryImagePreviews : selectedCard.galleryImageUrls} 
                    latitude={selectedCard.latitude} 
                    longitude={selectedCard.longitude} 
                    showGallery={selectedCard.showGallery} 
                    coverStyling={selectedCard.coverStyling} 
                    coverSections={selectedCard.coverSections} 
                    coverBackgroundStyle={selectedCard.coverBackgroundStyle} 
                    detailSections={selectedCard.detailSections} 
                    detailImagePreviews={detailImagePreviews} 
                    detailBackgroundColor={selectedCard.detailBackgroundColor}
                    showDetailOnly={true}
                    previewPlatform="ios"
                />
            </div>
            <div style={sizeLabelStyle}>
                This preview shows how the expanded card will appear on mobile devices. 
                <br />
                <strong>Scroll within the card to see all content, just like users will on their phones.</strong>
            </div>
        </div>
    );
};

  const isMobile = window.innerWidth <= 768;
  const styles = {
    container: { 
      padding: isMobile ? '10px' : '20px', 
      maxWidth: '1600px',  // Increased max width for more space
      margin: '0 auto', 
      width: '100%',
      boxSizing: 'border-box',
      overflow: 'visible'  // Prevent container from clipping content
    },
    formAndPreviewContainer: { 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row', 
      gap: isMobile ? '20px' : '40px',  // Increased gap
      marginBottom: '30px', 
      alignItems: 'flex-start', 
      flexWrap: 'nowrap',  // Prevent wrapping to maintain side-by-side layout
      overflow: 'visible'  // Allow overflow to prevent cutoff
    },
    formContainer: { 
      flex: isMobile ? '1' : '2', 
      minWidth: isMobile ? '100%' : '300px', 
      maxWidth: '100%', 
      borderRight: isMobile ? 'none' : '1px solid #eee', 
      paddingRight: isMobile ? '0' : '30px',
      paddingBottom: isMobile ? '20px' : '0',
      borderBottom: isMobile ? '1px solid #eee' : 'none'
    },
    previewContainerOuter: { 
      flex: 1, 
      minWidth: isMobile ? '100%' : '450px',  // Increased minimum width for preview
      maxWidth: '100%', 
      position: isMobile ? 'relative' : 'sticky', 
      top: isMobile ? '0' : '20px', 
      maxHeight: isMobile ? 'none' : '90vh', 
      overflowY: isMobile ? 'visible' : 'auto',
      overflowX: 'visible',  // Allow horizontal overflow to prevent cutoff
      paddingRight: '20px'  // Add padding to prevent edge cutoff
    },
    listContainer: { marginTop: '30px' },
    table: { 
      width: '100%', 
      borderCollapse: 'collapse', 
      overflowX: 'auto', 
      display: 'block',
      fontSize: isMobile ? '12px' : '14px'
    },
    th: { 
      border: '1px solid #ddd', 
      padding: isMobile ? '6px' : '10px', 
      backgroundColor: '#f2f2f2', 
      textAlign: 'left',
      fontSize: isMobile ? '11px' : '13px',
      whiteSpace: 'nowrap'
    },
    td: { 
      border: '1px solid #ddd', 
      padding: isMobile ? '6px' : '10px', 
      textAlign: 'left', 
      verticalAlign: 'top',
      fontSize: isMobile ? '11px' : '13px'
    },
    actionButton: { marginRight: '8px', padding: '6px 12px', cursor: 'pointer', borderRadius: '4px', border: '1px solid transparent' },
    imageInTable: { maxWidth: '100px', maxHeight: '70px', objectFit: 'cover', borderRadius: '4px' },
    formGroup: { marginBottom: '18px' },
    label: { display: 'block', marginBottom: '6px', fontWeight: 'bold', fontSize: '14px' },
    input: { 
      width: '100%', 
      padding: isMobile ? '8px' : '10px', 
      border: '1px solid #ccc', 
      borderRadius: '4px', 
      boxSizing: 'border-box', 
      fontSize: isMobile ? '16px' : '14px'  // 16px prevents zoom on iOS
    },
    textarea: { 
      width: '100%', 
      padding: isMobile ? '8px' : '10px', 
      border: '1px solid #ccc', 
      borderRadius: '4px', 
      minHeight: isMobile ? '80px' : '100px', 
      boxSizing: 'border-box', 
      fontSize: isMobile ? '16px' : '14px'
    },
    select: { 
      width: '100%', 
      padding: isMobile ? '8px' : '10px', 
      border: '1px solid #ccc', 
      borderRadius: '4px', 
      boxSizing: 'border-box', 
      fontSize: isMobile ? '16px' : '14px'
    },
    button: { padding: '12px 22px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '16px'},
    disabledButton: { backgroundColor: '#ccc', cursor: 'not-allowed' },
    imagePreviewContainer: { marginTop: '10px', border: '1px dashed #ccc', padding: '10px', borderRadius: '4px' },
    formImagePreview: { maxWidth: '100%', maxHeight: '200px', display: 'block', margin: '10px auto', borderRadius: '8px', border: '1px solid #eee' },
    galleryPreviewContainer: { display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '10px' },
    galleryImagePreviewItem: { position: 'relative', border: '1px solid #ddd', padding: '5px', borderRadius: '4px' },
    galleryImageInForm: { 
      width: isMobile ? '80px' : '100px', 
      height: isMobile ? '80px' : '100px', 
      objectFit: 'cover', 
      borderRadius: '4px' 
    },
    deleteGalleryImageBtn: { position: 'absolute', top: '-5px', right: '-5px', background: 'rgba(255,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer', lineHeight: '20px', textAlign: 'center'},
    progressBarContainer: { width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginTop: '5px', height: '10px', overflow: 'hidden'},
    progressBar: { height: '100%', backgroundColor: '#007bff', borderRadius: '4px', transition: 'width 0.3s ease-in-out'},
    error: { color: 'red', fontWeight: 'bold', padding: '12px', border: '1px solid red', borderRadius: '4px', backgroundColor: '#ffebee', textAlign: 'center' },
    success: { color: 'green', marginTop: '15px', fontWeight: 'bold', padding: '12px', border: '1px solid green', borderRadius: '4px', backgroundColor: '#e8f5e9' },
    checkboxContainer: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' },
    checkboxInput: { width: 'auto', marginRight: '5px', accentColor: '#007bff'},
    inlineFormGroup: { display: 'flex', gap: '10px', alignItems: 'center' },
    flexChild: { flex: 1 },
    tabContainer: { 
      display: 'flex', 
      borderBottom: '2px solid #e0e0e0', 
      marginBottom: isMobile ? '15px' : '20px', 
      gap: '0', 
      overflowX: 'auto', 
      minHeight: isMobile ? '40px' : '48px',
      WebkitOverflowScrolling: 'touch'
    },
    tab: { 
      padding: isMobile ? '10px 15px' : '12px 20px', 
      cursor: 'pointer', 
      backgroundColor: 'transparent', 
      border: 'none', 
      borderBottom: '3px solid transparent', 
      fontSize: isMobile ? '14px' : '16px', 
      fontWeight: '500', 
      transition: 'all 0.3s', 
      flex: '0 0 auto', 
      minWidth: isMobile ? '70px' : '80px', 
      whiteSpace: 'nowrap' 
    },
    activeTab: { borderBottomColor: '#007bff', color: '#007bff', backgroundColor: '#f0f8ff' },
    tabContent: { padding: '10px 0' },
    stylingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '15px' },
    stylingGroup: { border: '1px solid #e0e0e0', padding: '10px', borderRadius: '4px' },
    smallSelect: { width: '100%', padding: '6px', fontSize: '13px', border: '1px solid #ccc', borderRadius: '4px' },
    prioritySlider: { width: '100%', marginTop: '5px' },
    mosaicSizeContainer: { display: 'flex', gap: '10px', marginTop: '5px' },
    mosaicSizeOption: { padding: '8px 16px', border: '2px solid #ccc', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' },
    mosaicSizeSelected: { borderColor: '#007bff', backgroundColor: '#e3f2fd' },
    detailSection: { border: '1px solid #ddd', padding: '15px', borderRadius: '8px', marginBottom: '15px', position: 'relative' },
    detailSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    detailMoveButtons: { display: 'flex', gap: '5px' },
    smallButton: { padding: '4px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer', backgroundColor: '#f5f5f5' },
    dangerButton: { backgroundColor: '#ff4444', color: 'white', border: 'none' },
    coverSection: { border: '1px solid #ddd', padding: '15px', borderRadius: '8px', marginBottom: '15px', backgroundColor: '#fafafa' },
    coverSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' },
    smallInput: { width: '100%', padding: '4px 8px', fontSize: '12px', border: '1px solid #ccc', borderRadius: '3px' },
    searchBarContainer: { 
      display: 'flex', 
      alignItems: 'center', 
      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
      borderRadius: '8px', 
      padding: '10px 15px', 
      marginBottom: '20px', 
      border: '1px solid #ddd',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    searchIcon: { marginRight: '10px', color: '#666' },
    searchInput: { 
      flex: 1, 
      fontSize: '14px', 
      color: '#333', 
      border: 'none',
      outline: 'none',
      backgroundColor: 'transparent'
    },
    noResultsContainer: {
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
      margin: '20px 0'
    },
    noResultsText: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '5px'
    },
    noResultsSubtext: {
      fontSize: '14px',
      color: '#999'
    },
    highlightedRow: {
      backgroundColor: '#fffbf0',
      border: '2px solid #ffeb3b'
    }
  };

  if (isLoading) return <div style={{ padding: 24, textAlign: 'center', fontSize: '18px' }}>Loading Explore Cards...</div>;

  return (
    <div style={styles.container}>
      <h1>Manage Explore Cards</h1>
      {isSubmitting && <p style={{ color: 'blue', textAlign:'center', fontSize: '16px', margin:'10px 0' }}>Processing your request...</p>}
      {isConverting && <p style={{ color: '#1976d2', textAlign:'center', fontSize: '14px', margin:'10px 0' }}>Converting image to WebP format...</p>}
      {successMessage && <p style={styles.success}>{successMessage}</p>}
      <div style={styles.formAndPreviewContainer}>
        <div style={styles.formContainer}>
          <h3>
            {selectedCard.id ? 'Edit Explore Card' : 'Add New Explore Card'}
            {selectedCard.id && selectedCard.isHidden && (
              <span style={{ 
                marginLeft: '10px', 
                fontSize: '14px', 
                color: '#dc3545', 
                fontWeight: 'normal' 
              }}>
                (Hidden)
              </span>
            )}
          </h3>
          <div style={styles.tabContainer}>
            <button style={{ ...styles.tab, ...(activeTab === 'cover' ? styles.activeTab : {}) }} onClick={() => { setActiveTab('cover'); setTagInput(''); }}>Cover</button>
            <button style={{ ...styles.tab, ...(activeTab === 'details' ? styles.activeTab : {}) }} onClick={() => { setActiveTab('details'); setTagInput(''); }}>Details</button>
            <button style={{ ...styles.tab, ...(activeTab === 'tags' ? styles.activeTab : {}) }} onClick={() => { setActiveTab('tags'); setTagInput(''); }}>Tags</button>
          </div>
          <form onSubmit={handleSave}>
            {activeTab === 'cover' && (
              <div style={styles.tabContent}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Mosaic Placement Priority (1-10):</label>
                  <input type="range" min="1" max="10" name="mosaicPriority" value={selectedCard.mosaicPriority} onChange={(e) => handleInputChange({ target: { name: e.target.name, value: parseInt(e.target.value) }})} style={styles.prioritySlider} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                    <span>Low (1)</span><span>Current: {selectedCard.mosaicPriority}</span><span>High (10)</span>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <div style={styles.checkboxContainer}>
                    <input type="checkbox" id="isHidden" name="isHidden" style={styles.checkboxInput} checked={selectedCard.isHidden} onChange={handleInputChange} />
                    <label htmlFor="isHidden" style={{ ...styles.label, marginBottom: 0 }}>Hide this card from Explore screen</label>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px', marginLeft: '25px' }}>Hidden cards won't appear on the Explore screen but remain in the database.</p>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Card Visibility:</label>
                  <select
                    name="visibilityType"
                    value={selectedCard.visibilityType || 'all_passes'}
                    onChange={handleInputChange}
                    style={styles.input}
                  >
                    <option value="no_pass">No Pass Only (shown to users without journeys)</option>
                    <option value="all_passes">All Passes (shown to all journey holders)</option>
                    <option value="specific_routes">Specific Routes Only</option>
                  </select>

                  {selectedCard.visibilityType === 'specific_routes' && (
                    <>
                      <label style={styles.label}>Select Routes:</label>
                      <div style={{
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        padding: '10px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        backgroundColor: '#f9f9f9'
                      }}>
                        {routesList.length === 0 ? (
                          <p style={{ fontSize: '12px', color: '#999', margin: 0 }}>Loading routes...</p>
                        ) : (
                          routesList.sort((a, b) => a.name.localeCompare(b.name)).map(route => (
                            <label key={route.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: '8px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}>
                              <input
                                type="checkbox"
                                checked={selectedCard.assignedRoutes?.includes(route.id) || false}
                                onChange={(e) => {
                                  const currentRoutes = selectedCard.assignedRoutes || [];
                                  const newRoutes = e.target.checked
                                    ? [...currentRoutes, route.id]
                                    : currentRoutes.filter(id => id !== route.id);
                                  handleInputChange({ target: { name: 'assignedRoutes', value: newRoutes }});
                                }}
                                style={{ marginRight: '8px' }}
                              />
                              {route.name}
                              {route.storeStatus === 'hidden' && <span style={{ color: '#999', marginLeft: '5px' }}>(Hidden)</span>}
                              {route.storeStatus === 'beta' && <span style={{ color: '#ff9800', marginLeft: '5px' }}>(Inaugural)</span>}
                            </label>
                          ))
                        )}
                      </div>
                    </>
                  )}
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    • No Pass: Shows only to users without any journey<br/>
                    • All Passes: Shows to all users with journeys<br/>
                    • Specific Routes: Shows only on selected routes (plus All Pass cards)
                  </p>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Allowed Mosaic Sizes:</label>
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>Select which sizes this card can appear as in the mosaic. At least one must be selected.</p>
                  <div style={styles.mosaicSizeContainer}>
                    <div style={{ ...styles.mosaicSizeOption, ...(selectedCard.mosaicOptions.allowedSizes.includes('1x1') ? styles.mosaicSizeSelected : {}) }} onClick={() => handleAllowedSizeToggle('1x1')}>1×1 (Square)</div>
                    <div style={{ ...styles.mosaicSizeOption, ...(selectedCard.mosaicOptions.allowedSizes.includes('1x2') ? styles.mosaicSizeSelected : {}) }} onClick={() => handleAllowedSizeToggle('1x2')}>1×2 (Tall)</div>
                    <div style={{ ...styles.mosaicSizeOption, ...(selectedCard.mosaicOptions.allowedSizes.includes('2x3') ? styles.mosaicSizeSelected : {}) }} onClick={() => handleAllowedSizeToggle('2x3')}>2×3 (Large)</div>
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <h4 style={{ fontSize: '16px', marginBottom: '10px' }}>Cover Overlay</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Overlay Type:</label>
                      <select style={styles.smallSelect} value={selectedCard.coverBackgroundStyle.overlay} onChange={(e) => setSelectedCard(prev => ({ ...prev, coverBackgroundStyle: { ...prev.coverBackgroundStyle, overlay: e.target.value } }))}>
                        <option value="none">None</option>
                        <option value="solid">Solid Color</option>
                      </select>
                    </div>
                    {selectedCard.coverBackgroundStyle.overlay !== 'none' && (
                      <>
                        <div>
                          <ColorPicker
                            label="Overlay Color:"
                            value={selectedCard.coverBackgroundStyle.overlayColor}
                            onChange={(color) => setSelectedCard(prev => ({ ...prev, coverBackgroundStyle: { ...prev.coverBackgroundStyle, overlayColor: color }}))}
                            placeholder="rgba(0,0,0,0.3)"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <h4 style={{ fontSize: '16px', marginBottom: '10px' }}>Cover Elements</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>Customize the elements that appear on the card cover. Default elements can be disabled but not removed.</p>
                {selectedCard.coverSections.map((section, index) => (
                  <div key={section.id} style={styles.coverSection}>
                    <div style={styles.coverSectionHeader}>
                      <h5 style={{ margin: 0, fontSize: '14px' }}>{section.id.startsWith('default-') ? `${section.type.charAt(0).toUpperCase() + section.type.slice(1)} (Default)` : `Custom Element ${index + 1}`}</h5>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', marginRight: '10px' }}>
                          <input type="checkbox" checked={section.enabled} onChange={(e) => updateCoverSection(section.id, 'enabled', e.target.checked)} style={{ marginRight: '5px' }} />
                          <span style={{ fontSize: '12px' }}>Enabled</span>
                        </label>
                        <div style={styles.detailMoveButtons}>
                          <button type="button" style={styles.smallButton} onClick={() => moveCoverSection(section.id, 'up')} disabled={index === 0}>↑</button>
                          <button type="button" style={styles.smallButton} onClick={() => moveCoverSection(section.id, 'down')} disabled={index === selectedCard.coverSections.length - 1}>↓</button>
                        </div>
                        {!section.id.startsWith('default-') && <button type="button" style={{ ...styles.smallButton, ...styles.dangerButton }} onClick={() => removeCoverSection(section.id)}>Remove</button>}
                      </div>
                    </div>
                    {section.enabled && (
                      <div style={{ marginTop: '10px' }}>
                        {!section.id.startsWith('default-') && (
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Type:</label>
                            <select style={styles.smallSelect} value={section.type} onChange={(e) => updateCoverSection(section.id, 'type', e.target.value)}>
                              <option value="customText">Custom Text</option><option value="badge">Badge</option>
                            </select>
                          </div>
                        )}
                        {section.field && (
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Content:</label>
                            {section.field === 'title' && <input type="text" style={styles.input} value={selectedCard.title} onChange={(e) => handleInputChange({ target: { name: 'title', value: e.target.value }})} placeholder="Enter title..." />}
                            {section.field === 'locationTag' && <input type="text" style={styles.input} value={selectedCard.locationTag} onChange={(e) => handleInputChange({ target: { name: 'locationTag', value: e.target.value }})} placeholder="Enter location..." />}
                            {section.field === 'dates' && (
                              <div style={styles.inlineFormGroup}>
                                <div style={styles.flexChild}>
                                  <label style={{ fontSize: '11px' }}>Start:</label>
                                  <input type="date" style={styles.input} value={selectedCard.startDate} onChange={(e) => handleInputChange({ target: { name: 'startDate', value: e.target.value }})} />
                                </div>
                                <div style={styles.flexChild}>
                                  <label style={{ fontSize: '11px' }}>End:</label>
                                  <input type="date" style={styles.input} value={selectedCard.endDate} onChange={(e) => handleInputChange({ target: { name: 'endDate', value: e.target.value }})} />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {!section.field && (
                          <div style={{ marginBottom: '10px' }}>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Content:</label>
                            <input type="text" style={{ ...styles.input, maxWidth: '350px' }} value={section.content} onChange={(e) => updateCoverSection(section.id, 'content', e.target.value)} placeholder={section.type === 'badge' ? 'Badge text...' : 'Enter text...'} maxLength={section.type === 'badge' ? 30 : 50} />
                            <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>{(section.content || '').length}/{section.type === 'badge' ? 30 : 50} characters</div>
                          </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px' }}>
                          <div>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>H-Align Preset:</label>
                            <select style={styles.smallSelect} value={section.styling.align} onChange={(e) => {
                              const align = e.target.value;
                              updateCoverSectionStyling(section.id, 'align', align);
                              // Apply margin presets based on alignment
                              if (align === 'left') {
                                updateCoverSectionStyling(section.id, 'marginLeft', 20);
                                updateCoverSectionStyling(section.id, 'marginRight', 0);
                              } else if (align === 'right') {
                                updateCoverSectionStyling(section.id, 'marginLeft', 0);
                                updateCoverSectionStyling(section.id, 'marginRight', 20);
                              } else { // center
                                updateCoverSectionStyling(section.id, 'marginLeft', 0);
                                updateCoverSectionStyling(section.id, 'marginRight', 0);
                              }
                            }}>
                              <option value="left">Left</option><option value="center">Center</option><option value="right">Right</option>
                            </select>
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>V-Align Preset:</label>
                            <select style={styles.smallSelect} value={section.styling.verticalAlign} onChange={(e) => {
                              const vAlign = e.target.value;
                              updateCoverSectionStyling(section.id, 'verticalAlign', vAlign);
                              // Apply margin presets based on vertical alignment
                              if (vAlign === 'top') {
                                updateCoverSectionStyling(section.id, 'marginTop', 20);
                                updateCoverSectionStyling(section.id, 'marginBottom', 0);
                              } else if (vAlign === 'bottom') {
                                updateCoverSectionStyling(section.id, 'marginTop', 0);
                                updateCoverSectionStyling(section.id, 'marginBottom', 20);
                              } else { // middle
                                updateCoverSectionStyling(section.id, 'marginTop', 0);
                                updateCoverSectionStyling(section.id, 'marginBottom', 0);
                              }
                            }}>
                              <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
                            </select>
                          </div>
                          {/* Hide size control for title sections - using responsive Barlow sizing */}
                          {section.type !== 'title' && (
                            <div>
                              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Size (px):</label>
                              <input 
                                type="number" 
                                style={styles.smallInput} 
                                value={section.styling.size !== undefined ? section.styling.size : ''} 
                                onChange={(e) => {
                                  const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                                  updateCoverSectionStyling(section.id, 'size', value);
                                }}
                                placeholder="16"
                              />
                            </div>
                          )}
                          {section.type === 'title' && (
                            <div>
                              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#666' }}>Size:</label>
                              <div style={{ fontSize: '12px', color: '#999', padding: '6px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                Auto (Responsive)
                              </div>
                            </div>
                          )}
                          <div>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Color:</label>
                            <select style={styles.smallSelect} value={section.styling.color === 'white' || section.styling.color === 'black' ? section.styling.color : 'custom'} onChange={(e) => {
                              if (e.target.value === 'custom') {
                                updateCoverSectionStyling(section.id, 'color', '#000000');
                              } else {
                                updateCoverSectionStyling(section.id, 'color', e.target.value);
                              }
                            }}>
                              <option value="white">White</option>
                              <option value="black">Black</option>
                              <option value="custom">Custom</option>
                            </select>
                          </div>
                          {section.styling.color !== 'white' && section.styling.color !== 'black' && (
                            <div>
                              <ColorPicker
                                label="Custom Color:"
                                value={section.styling.color}
                                onChange={(color) => updateCoverSectionStyling(section.id, 'color', color)}
                                placeholder="#000000"
                              />
                            </div>
                          )}
                          {/* Hide font controls for title sections - always use Barlow-Bold */}
                          {section.type !== 'title' && (
                            <>
                              <div>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Weight:</label>
                                <select style={styles.smallSelect} value={section.styling.fontWeight} onChange={(e) => updateCoverSectionStyling(section.id, 'fontWeight', e.target.value)}>
                                  <option value="normal">Normal</option><option value="600">Semi-Bold</option><option value="bold">Bold</option>
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Font:</label>
                                <select style={styles.smallSelect} value={section.styling.font || 'Caveat-Bold'} onChange={(e) => updateCoverSectionStyling(section.id, 'font', e.target.value)}>
                                  <option value="AirbnbCereal_W_Lt">Airbnb Light</option>
                                  <option value="AirbnbCereal_W_Md">Airbnb Medium</option>
                                  <option value="PassFont">Pass Font</option>
                                  <option value="MainFontLight">Main Light</option>
                                  <option value="Caveat-Bold">Caveat Bold</option>
                                  <option value="Barlow-Bold">Barlow Bold</option>
                                  <option value="system">System Default</option>
                                </select>
                              </div>
                            </>
                          )}
                          {section.type === 'title' && (
                            <>
                              <div>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#666' }}>Weight:</label>
                                <div style={{ fontSize: '12px', color: '#999', padding: '6px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                  Bold
                                </div>
                              </div>
                              <div>
                                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: '#666' }}>Font:</label>
                                <div style={{ fontSize: '12px', color: '#999', padding: '6px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                                  Barlow
                                </div>
                              </div>
                            </>
                          )}
                          <div>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Opacity:</label>
                            <input type="range" min="0" max="1" step="0.1" value={section.styling.opacity} onChange={(e) => updateCoverSectionStyling(section.id, 'opacity', parseFloat(e.target.value))} style={{ width: '100%' }} />
                            <span style={{ fontSize: '10px', color: '#666' }}>{section.styling.opacity}</span>
                          </div>
                          <div style={{ gridColumn: 'span 3' }}>
                            <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                              <input type="checkbox" checked={section.styling.textShadow} onChange={(e) => updateCoverSectionStyling(section.id, 'textShadow', e.target.checked)} style={{ marginRight: '5px' }} />
                              Text Shadow (for readability)
                            </label>
                          </div>
                        </div>
                        <div style={{ marginTop: '15px', borderTop: '1px solid #e0e0e0', paddingTop: '15px' }}>
                          <h6 style={{ fontSize: '13px', margin: '0 0 10px 0', fontWeight: 'bold' }}>Margins (px):</h6>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                            <div>
                              <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Top:</label>
                              <input type="number" style={styles.smallInput} value={section.styling.marginTop || 0} onChange={(e) => updateCoverSectionStyling(section.id, 'marginTop', parseInt(e.target.value) || 0)} />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Right:</label>
                              <input type="number" style={styles.smallInput} value={section.styling.marginRight || 0} onChange={(e) => updateCoverSectionStyling(section.id, 'marginRight', parseInt(e.target.value) || 0)} />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Bottom:</label>
                              <input type="number" style={styles.smallInput} value={section.styling.marginBottom || 0} onChange={(e) => updateCoverSectionStyling(section.id, 'marginBottom', parseInt(e.target.value) || 0)} />
                            </div>
                            <div>
                              <label style={{ fontSize: '11px', display: 'block', marginBottom: '2px' }}>Left:</label>
                              <input type="number" style={styles.smallInput} value={section.styling.marginLeft || 0} onChange={(e) => updateCoverSectionStyling(section.id, 'marginLeft', parseInt(e.target.value) || 0)} />
                            </div>
                          </div>
                        </div>
                        {section.type === 'badge' && (
                          <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            <h6 style={{ fontSize: '13px', margin: '0 0 10px 0' }}>Badge Styling</h6>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                              <div>
                                <ColorPicker
                                  label="Background:"
                                  value={section.styling.backgroundColor || 'rgba(255,255,255,0.25)'}
                                  onChange={(color) => updateCoverSectionStyling(section.id, 'backgroundColor', color)}
                                  placeholder="rgba(255,255,255,0.25)"
                                />
                              </div>
                              <div>
                                <label style={{ fontSize: '11px' }}>Border Radius:</label>
                                <input type="number" style={styles.smallInput} value={section.styling.borderRadius || 10} onChange={(e) => updateCoverSectionStyling(section.id, 'borderRadius', parseInt(e.target.value))} />
                              </div>
                              <div>
                                <label style={{ fontSize: '11px' }}>Padding:</label>
                                <input type="text" style={styles.smallInput} value={section.styling.padding || '4px 10px'} onChange={(e) => updateCoverSectionStyling(section.id, 'padding', e.target.value)} placeholder="4px 10px" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <button type="button" style={{ ...styles.button, backgroundColor: '#28a745', fontSize: '14px', padding: '8px 16px' }} onClick={addCoverSection}>+ Add Cover Element</button>
                <div style={styles.formGroup}>
                  <label htmlFor="exploreCardImageFile" style={styles.label}>Cover Image: (Max 5MB, will be converted to WebP)</label>
                  <input type="file" id="exploreCardImageFile" style={styles.input} accept="image/*" onChange={handleCoverImageChange} />
                  {isSubmitting && coverImageFile && uploadProgress > 0 && uploadProgress < 100 && (
                    <div style={styles.progressBarContainer}><div style={{...styles.progressBar, width: `${uploadProgress}%`}}></div></div>
                  )}
                  {coverImagePreview && <img src={coverImagePreview} alt="Cover preview" style={styles.formImagePreview} />}
                </div>
              </div>
            )}
            {activeTab === 'tags' && (
              <div style={styles.tabContent}>
                <h4>Tags</h4>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                  Add searchable tags to help users find this card. Tags are lowercase with hyphens for spaces. Maximum 13 tags.
                </p>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Add New Tag:</label>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        style={styles.input}
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyPress={handleTagInputKeyPress}
                        placeholder="Enter tag (e.g., beach-vacation)"
                        maxLength={30}
                      />
                      <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                        Tip: Spaces will be converted to hyphens. Special characters will be removed.
                      </div>
                    </div>
                    <button
                      type="button"
                      style={{ ...styles.button, padding: '10px 20px', fontSize: '14px' }}
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || selectedCard.tags.length >= 13}
                    >
                      Add Tag
                    </button>
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Current Tags ({selectedCard.tags.length}/13):
                  </label>
                  {selectedCard.tags.length === 0 ? (
                    <p style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
                      No tags added yet
                    </p>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {selectedCard.tags.map((tag, index) => (
                        <div
                          key={index}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#e3f2fd',
                            color: '#1976d2',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '14px',
                            border: '1px solid #90caf9',
                          }}
                        >
                          <span>{tag}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            style={{
                              marginLeft: '8px',
                              background: 'none',
                              border: 'none',
                              color: '#1976d2',
                              cursor: 'pointer',
                              fontSize: '16px',
                              lineHeight: '16px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '16px',
                              height: '16px',
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {allExistingTags.length > 0 && (
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Existing Tags (click to add):</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {allExistingTags
                        .filter(tag => !selectedCard.tags.includes(tag))
                        .map((tag, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => {
                              if (selectedCard.tags.length < 13) {
                                setSelectedCard(prev => ({ ...prev, tags: [...prev.tags, tag] }));
                              } else {
                                setError('Maximum 13 tags allowed per card');
                              }
                            }}
                            style={{
                              padding: '4px 10px',
                              backgroundColor: '#f5f5f5',
                              border: '1px solid #ddd',
                              borderRadius: '12px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#e0e0e0';
                              e.target.style.borderColor = '#bbb';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = '#f5f5f5';
                              e.target.style.borderColor = '#ddd';
                            }}
                            disabled={selectedCard.tags.length >= 13}
                          >
                            + {tag}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'details' && (
  <div style={styles.tabContent}>
    <h4>Detail View Settings</h4>
    <div style={styles.formGroup}>
      <ColorPicker
        label="Detail Background Color:"
        value={selectedCard.detailBackgroundColor || '#ffffff'}
        onChange={(color) => setSelectedCard(prev => ({ ...prev, detailBackgroundColor: color }))}
        placeholder="#ffffff or rgba(255,255,255,1)"
      />
      <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>This color will be used as the background for the detail view when the card is expanded.</p>
    </div>
    
    <h4 style={{ marginTop: '30px' }}>Custom Detail Sections</h4>
    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>Add custom sections to the detail view. These will appear when the card is expanded.</p>
    {selectedCard.detailSections.map((section, index) => (
      <div key={section.id} style={styles.detailSection}>
        <div style={styles.detailSectionHeader}>
          <h5 style={{ margin: 0, fontSize: '14px' }}>Section {index + 1}</h5>
          <div style={{ display: 'flex', gap: '5px' }}>
            <div style={styles.detailMoveButtons}>
              <button type="button" style={styles.smallButton} onClick={() => moveDetailSection(section.id, 'up')} disabled={index === 0}>↑</button>
              <button type="button" style={styles.smallButton} onClick={() => moveDetailSection(section.id, 'down')} disabled={index === selectedCard.detailSections.length - 1}>↓</button>
            </div>
            <button type="button" style={{ ...styles.smallButton, ...styles.dangerButton }} onClick={() => removeDetailSection(section.id)}>Remove</button>
          </div>
        </div>
        
        {/* Basic Settings Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Type:</label>
            <select style={styles.smallSelect} value={section.type} onChange={(e) => updateDetailSection(section.id, 'type', e.target.value)}>
              <option value="title">Title</option>
              <option value="text">Text Box</option>
              <option value="description">Description</option>
              <option value="image">Image</option>
              <option value="carousel">Image Carousel</option>
              <option value="backgroundImage">Background Image</option>
              <option value="link">Link</option>
              <option value="map">Map Coordinates</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>H-Align Preset:</label>
            <select style={styles.smallSelect} value={section.styling.align} onChange={(e) => {
              const align = e.target.value;
              updateDetailSectionStyling(section.id, 'align', align);
              // Apply margin presets based on alignment
              if (align === 'left') {
                updateDetailSectionStyling(section.id, 'marginLeft', 20);
                updateDetailSectionStyling(section.id, 'marginRight', 20);
              } else if (align === 'right') {
                updateDetailSectionStyling(section.id, 'marginLeft', 20);
                updateDetailSectionStyling(section.id, 'marginRight', 20);
              } else { // center
                updateDetailSectionStyling(section.id, 'marginLeft', 20);
                updateDetailSectionStyling(section.id, 'marginRight', 20);
              }
            }}>
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>V-Spacing Preset:</label>
            <select style={styles.smallSelect} value="custom" onChange={(e) => {
              const spacing = e.target.value;
              // Apply margin presets based on spacing
              if (spacing === 'compact') {
                updateDetailSectionStyling(section.id, 'marginTop', 10);
                updateDetailSectionStyling(section.id, 'marginBottom', 10);
              } else if (spacing === 'normal') {
                updateDetailSectionStyling(section.id, 'marginTop', 20);
                updateDetailSectionStyling(section.id, 'marginBottom', 20);
              } else if (spacing === 'spacious') {
                updateDetailSectionStyling(section.id, 'marginTop', 30);
                updateDetailSectionStyling(section.id, 'marginBottom', 30);
              }
            }}>
              <option value="custom">Custom</option>
              <option value="compact">Compact (10px)</option>
              <option value="normal">Normal (20px)</option>
              <option value="spacious">Spacious (30px)</option>
            </select>
          </div>
          {(section.type === 'title' || section.type === 'text' || section.type === 'description') && (
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Size (px):</label>
              <input 
                type="number" 
                style={styles.smallInput} 
                value={section.styling.size !== undefined ? section.styling.size : ''} 
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseInt(e.target.value);
                  updateDetailSectionStyling(section.id, 'size', value);
                }}
                placeholder="16"
              />
            </div>
          )}
          {(section.type === 'title' || section.type === 'text' || section.type === 'description' || section.type === 'link') && (
            <>
              <div>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Color:</label>
                <select style={styles.smallSelect} value={section.styling.color === 'black' || section.styling.color === 'white' ? section.styling.color : 'custom'} onChange={(e) => {
                  if (e.target.value === 'custom') {
                    updateDetailSectionStyling(section.id, 'color', '#000000');
                  } else {
                    updateDetailSectionStyling(section.id, 'color', e.target.value);
                  }
                }}>
                  <option value="black">Black</option>
                  <option value="white">White</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {section.styling.color !== 'black' && section.styling.color !== 'white' && (
                <div>
                  <ColorPicker
                    label="Custom Color:"
                    value={section.styling.color}
                    onChange={(color) => updateDetailSectionStyling(section.id, 'color', color)}
                    placeholder="#000000"
                  />
                </div>
              )}
              <div>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Font:</label>
                <select style={styles.smallSelect} value={section.styling.font || 'system'} onChange={(e) => updateDetailSectionStyling(section.id, 'font', e.target.value)}>
                  <option value="AirbnbCereal_W_Lt">Airbnb Light</option>
                  <option value="AirbnbCereal_W_Md">Airbnb Medium</option>
                  <option value="PassFont">Pass Font</option>
                  <option value="MainFontLight">Main Light</option>
                  <option value="Caveat-Bold">Caveat Bold</option>
                  <option value="Barlow-Bold">Barlow Bold</option>
                  <option value="system">System Default</option>
                </select>
              </div>
            </>
          )}
        </div>

        {/* CONTENT INPUTS SECTION - THIS WAS MISSING! */}
        <div style={{ marginTop: '15px' }}>
          {(section.type === 'title' || section.type === 'text' || section.type === 'description' || section.type === 'link') && (
            <>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Content:</label>
              {(section.type === 'text' || section.type === 'description') ? (
                <textarea 
                  style={styles.textarea} 
                  value={section.content} 
                  onChange={(e) => updateDetailSection(section.id, 'content', e.target.value)} 
                  placeholder={section.type === 'description' ? 'Enter description...' : 'Enter text content...'} 
                />
              ) : (
                <input 
                  type={section.type === 'link' ? 'url' : 'text'} 
                  style={styles.input} 
                  value={section.content} 
                  onChange={(e) => updateDetailSection(section.id, 'content', e.target.value)} 
                  placeholder={section.type === 'link' ? 'https://example.com' : 'Enter title...'} 
                />
              )}
            </>
          )}
          
          {(section.type === 'image' || section.type === 'backgroundImage') && (
            <>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Upload Image: (will be converted to WebP)</label>
              <input type="file" accept="image/*" style={styles.input} onChange={(e) => handleDetailImageChange(section.id, e.target.files)} />
              {(detailImagePreviews[section.id] || section.content) && (
                <img src={detailImagePreviews[section.id] || section.content} alt="Section preview" style={{ ...styles.formImagePreview, maxHeight: '100px', marginTop: '10px' }} />
              )}
            </>
          )}
          
          {section.type === 'carousel' && (
            <>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Select images from gallery:</label>
              <p style={{ fontSize: '11px', color: '#666', marginBottom: '10px' }}>Upload images to the gallery first, then select them here for the carousel.</p>
              <div style={styles.galleryPreviewContainer}>
                {selectedCard.galleryImageUrls.map((url, idx) => (
                  <div 
                    key={idx} 
                    style={{ 
                      ...styles.galleryImagePreviewItem, 
                      border: (section.content || []).includes(url) ? '3px solid #007bff' : '1px solid #ddd', 
                      cursor: 'pointer' 
                    }} 
                    onClick={() => { 
                      const currentUrls = section.content || []; 
                      const newUrls = currentUrls.includes(url) ? currentUrls.filter(u => u !== url) : [...currentUrls, url]; 
                      updateDetailSection(section.id, 'content', newUrls); 
                    }}
                  >
                    <img src={url} alt={`Gallery ${idx + 1}`} style={styles.galleryImageInForm} />
                  </div>
                ))}
              </div>
            </>
          )}
          
          {section.type === 'map' && (
            <>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Map Coordinates:</label>
              <div style={styles.inlineFormGroup}>
                <div style={styles.flexChild}>
                  <label style={{ fontSize: '11px' }}>Latitude:</label>
                  <input 
                    type="number" 
                    step="any" 
                    style={styles.input} 
                    value={section.content?.latitude || ''} 
                    onChange={(e) => updateDetailSection(section.id, 'content', { ...section.content, latitude: e.target.value })} 
                    placeholder="e.g., 34.0522" 
                  />
                </div>
                <div style={styles.flexChild}>
                  <label style={{ fontSize: '11px' }}>Longitude:</label>
                  <input 
                    type="number" 
                    step="any" 
                    style={styles.input} 
                    value={section.content?.longitude || ''} 
                    onChange={(e) => updateDetailSection(section.id, 'content', { ...section.content, longitude: e.target.value })} 
                    placeholder="e.g., -118.2437" 
                  />
                </div>
              </div>
            </>
          )}

        </div>

        {/* Spacing & Margins */}
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
          <h6 style={{ fontSize: '13px', margin: '0 0 10px 0' }}>Spacing & Margins</h6>
          <div>
            <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Margins (px):</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Top:</label>
                <input type="number" style={styles.smallInput} value={section.styling.marginTop || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'marginTop', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Right:</label>
                <input type="number" style={styles.smallInput} value={section.styling.marginRight || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'marginRight', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Bottom:</label>
                <input type="number" style={styles.smallInput} value={section.styling.marginBottom || 20} onChange={(e) => updateDetailSectionStyling(section.id, 'marginBottom', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Left:</label>
                <input type="number" style={styles.smallInput} value={section.styling.marginLeft || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'marginLeft', parseInt(e.target.value) || 0)} />
              </div>
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Padding (px):</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Top:</label>
                <input type="number" style={styles.smallInput} value={section.styling.paddingTop || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'paddingTop', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Right:</label>
                <input type="number" style={styles.smallInput} value={section.styling.paddingRight || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'paddingRight', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Bottom:</label>
                <input type="number" style={styles.smallInput} value={section.styling.paddingBottom || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'paddingBottom', parseInt(e.target.value) || 0)} />
              </div>
              <div>
                <label style={{ fontSize: '10px', display: 'block', marginBottom: '2px' }}>Left:</label>
                <input type="number" style={styles.smallInput} value={section.styling.paddingLeft || 0} onChange={(e) => updateDetailSectionStyling(section.id, 'paddingLeft', parseInt(e.target.value) || 0)} />
              </div>
            </div>
          </div>
          
          {/* Platform-specific padding */}
          <div style={{ marginTop: '10px' }}>
            <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
              Platform Padding Override:
            </label>
            
            {/* iOS Padding */}
            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '10px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>iOS:</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Top:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.iosPaddingTop !== undefined ? section.styling.iosPaddingTop : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'iosPaddingTop', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Right:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.iosPaddingRight !== undefined ? section.styling.iosPaddingRight : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'iosPaddingRight', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Bottom:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.iosPaddingBottom !== undefined ? section.styling.iosPaddingBottom : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'iosPaddingBottom', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Left:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.iosPaddingLeft !== undefined ? section.styling.iosPaddingLeft : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'iosPaddingLeft', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
              </div>
            </div>
            
            {/* Android Padding */}
            <div>
              <label style={{ fontSize: '10px', fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Android:</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Top:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.androidPaddingTop !== undefined ? section.styling.androidPaddingTop : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'androidPaddingTop', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Right:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.androidPaddingRight !== undefined ? section.styling.androidPaddingRight : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'androidPaddingRight', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Bottom:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.androidPaddingBottom !== undefined ? section.styling.androidPaddingBottom : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'androidPaddingBottom', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
                <div>
                  <label style={{ fontSize: '9px', display: 'block', marginBottom: '2px' }}>Left:</label>
                  <input 
                    type="number" 
                    style={styles.smallInput} 
                    value={section.styling.androidPaddingLeft !== undefined ? section.styling.androidPaddingLeft : ''} 
                    placeholder="auto"
                    onChange={(e) => updateDetailSectionStyling(section.id, 'androidPaddingLeft', e.target.value === '' ? undefined : parseInt(e.target.value) || 0)} 
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
    
    <button type="button" style={{ ...styles.button, backgroundColor: '#28a745', fontSize: '14px', padding: '8px 16px' }} onClick={addDetailSection}>+ Add Detail Section</button>
    
    <div style={{ ...styles.formGroup, marginTop: '30px' }}>
      <h4 style={{ fontSize: '16px', marginBottom: '10px' }}>Gallery Images (Optional)</h4>
      <div style={styles.checkboxContainer}>
        <input type="checkbox" id="showGallery" name="showGallery" style={styles.checkboxInput} checked={selectedCard.showGallery} onChange={handleInputChange} />
        <label htmlFor="showGallery" style={{ ...styles.label, marginBottom: 0 }}>Enable gallery images in detail view</label>
      </div>
      {selectedCard.showGallery && (
        <>
          <label htmlFor="exploreCardGalleryFiles" style={{ ...styles.label, marginTop: '10px' }}>Upload Gallery Images (Max 5MB each, will be converted to WebP):</label>
          <input type="file" id="exploreCardGalleryFiles" multiple style={styles.input} accept="image/*" onChange={handleGalleryImageChange} />
          {isSubmitting && Object.keys(galleryUploadProgress).length > 0 && Object.entries(galleryUploadProgress).map(([fileName, progress]) => (
            progress < 100 && <div key={fileName} style={{marginTop: '5px'}}>
              <small>Uploading {fileName}: {Math.round(progress)}%</small>
              <div style={styles.progressBarContainer}><div style={{...styles.progressBar, width: `${progress}%`}}></div></div>
            </div>
          ))}
          {galleryImagePreviews.length > 0 && (
            <div style={styles.imagePreviewContainer}>
              <p style={{fontWeight:'bold', fontSize:'13px'}}>New Gallery Images Preview:</p>
              <div style={styles.galleryPreviewContainer}>
                {galleryImagePreviews.map((previewUrl, index) => (
                  <img key={index} src={previewUrl} alt={`New gallery preview ${index + 1}`} style={styles.galleryImageInForm} />
                ))}
              </div>
            </div>
          )}
          {selectedCard.galleryImageUrls && selectedCard.galleryImageUrls.length > 0 && (
            <div style={{...styles.imagePreviewContainer, marginTop: '15px'}}>
              <p style={{fontWeight:'bold', fontSize:'13px'}}>Current Gallery Images:</p>
              <div style={styles.galleryPreviewContainer}>
                {selectedCard.galleryImageUrls.map((url, index) => (
                  <div key={index} style={styles.galleryImagePreviewItem}>
                    <img src={url} alt={`Gallery item ${index + 1}`} style={styles.galleryImageInForm} />
                    <button type="button" style={styles.deleteGalleryImageBtn} title="Delete this image" onClick={() => handleDeleteGalleryImage(url, selectedCard.id)} disabled={isSubmitting}>&times;</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  </div>
)}
            <div style={{ marginTop: '30px' }}>
              {error && !isSubmitting && <p style={{ ...styles.error, marginBottom: '15px' }}>{error}</p>}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" style={isSubmitting || isConverting ? {...styles.button, ...styles.disabledButton} : styles.button} disabled={isSubmitting || isConverting}>{isSubmitting ? 'Saving...' : (selectedCard.id ? 'Update Card' : 'Add Card')}</button>
                {selectedCard.id && <button type="button" onClick={handleAddNew} style={{...styles.button, backgroundColor: '#6c757d'}} disabled={isSubmitting || isConverting}>Clear Form / New</button>}
              </div>
            </div>
          </form>
        </div>
        <div style={styles.previewContainerOuter}>
          <h3>Live Preview</h3>
          {selectedCard.isHidden && (
            <div style={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: '4px', 
              padding: '10px', 
              marginBottom: '15px',
              fontSize: '13px',
              color: '#856404',
              textAlign: 'center'
            }}>
              ⚠️ This card is currently hidden from the Explore screen
            </div>
          )}
          {activeTab === 'tags' && selectedCard.tags.length > 0 && (
            <div style={{ 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #90caf9', 
              borderRadius: '4px', 
              padding: '10px', 
              marginBottom: '15px',
              fontSize: '13px',
              color: '#1565c0',
            }}>
              <strong>Current Tags:</strong> {selectedCard.tags.join(', ')}
            </div>
          )}
          {activeTab === 'cover' ? <CoverPreviewWithSizeSelector selectedCard={selectedCard} coverImagePreview={coverImagePreview} /> : <DetailPreview selectedCard={selectedCard} coverImagePreview={coverImagePreview} galleryImagePreviews={galleryImagePreviews} detailImagePreviews={detailImagePreviews} />}
        </div>
      </div>
      <div style={styles.listContainer}>
        <h3>
          Existing Explore Cards ({filteredCardsList.length} of {cardsList.length})
          {filteredCardsList.length > 0 && (
            <span style={{ fontSize: '14px', fontWeight: 'normal', marginLeft: '10px', color: '#666' }}>
              ({filteredCardsList.filter(c => !c.isHidden).length} visible, {filteredCardsList.filter(c => c.isHidden).length} hidden)
            </span>
          )}
        </h3>
        
        {/* Search Bar */}
        <div style={styles.searchBarContainer}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="Search by title, tags, location, or description..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                color: '#999',
                fontSize: '20px',
                padding: '0 5px'
              }}
              onClick={() => setSearchText('')}
            >
              ×
            </button>
          )}
        </div>

        {searchText && filteredCardsList.length === 0 && (
          <div style={styles.noResultsContainer}>
            <div style={styles.noResultsText}>No results found for "{searchText}"</div>
            <div style={styles.noResultsSubtext}>Try searching for different keywords</div>
          </div>
        )}

        {filteredCardsList.length === 0 && !isLoading && !searchText && <p>No explore cards found. Add one above!</p>}
        
        {filteredCardsList.length > 0 && (
          <div style={{ 
            overflowX: 'auto', 
            WebkitOverflowScrolling: 'touch', 
            marginLeft: isMobile ? '-10px' : '0', 
            marginRight: isMobile ? '-10px' : '0', 
            paddingLeft: isMobile ? '10px' : '0', 
            paddingRight: isMobile ? '10px' : '0',
            paddingBottom: '10px'
          }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '5%' }}>Priority</th>
                <th style={{ ...styles.th, width: '10%' }}>Cover</th>
                <th style={{ ...styles.th, width: '12%' }}>Title</th>
                <th style={{ ...styles.th, width: '10%' }}>Location</th>
                <th style={{ ...styles.th, width: '10%' }}>Dates</th>
                <th style={{ ...styles.th, width: '12%' }}>Tags</th>
                <th style={{ ...styles.th, width: '10%' }}>Visibility</th>
                <th style={{ ...styles.th, width: '7%' }}>Sizes</th>
                <th style={{ ...styles.th, width: '6%' }}>Gallery</th>
                <th style={{ ...styles.th, width: '7%' }}>Status</th>
                <th style={{ ...styles.th, width: '15%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCardsList.map((card) => (
                <tr key={card.id} style={{ 
                  backgroundColor: card.isHidden ? '#f8f9fa' : 'transparent',
                  ...(card.searchScore && card.searchScore > 0 ? styles.highlightedRow : {})
                }}>
                  <td style={styles.td}>
                    {card.mosaicPriority || 5}
                    {card.searchScore && card.searchScore > 0 && (
                      <div style={{ fontSize: '10px', color: '#ff9800', marginTop: '2px' }}>
                        Score: {card.searchScore}
                      </div>
                    )}
                  </td>
                  <td style={styles.td}>{card.imageUrl ? <img src={card.imageUrl} alt={card.title || 'Card image'} style={{ ...styles.imageInTable, opacity: card.isHidden ? 0.6 : 1 }} /> : 'No Image'}</td>
                  <td style={styles.td}>{card.title || <small><i>No title</i></small>}</td>
                  <td style={styles.td}>{card.locationTag || <small><i>No location</i></small>}</td>
                  <td style={styles.td}>{card.startDate && card.endDate ? `${card.startDate} to ${card.endDate}` : <small><i>No dates</i></small>}</td>
                  <td style={styles.td}>
                    {card.tags && card.tags.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {card.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} style={{ 
                            fontSize: '11px', 
                            backgroundColor: '#e3f2fd', 
                            color: '#1976d2',
                            padding: '2px 6px', 
                            borderRadius: '10px',
                            whiteSpace: 'nowrap'
                          }}>
                            {tag}
                          </span>
                        ))}
                        {card.tags.length > 3 && (
                          <span style={{ fontSize: '11px', color: '#666' }}>+{card.tags.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      <small><i>No tags</i></small>
                    )}
                  </td>
                  <td style={styles.td}>
                    {card.visibilityType === 'no_pass' ? (
                      <small style={{ color: '#ff9800' }}>No Pass Only</small>
                    ) : card.visibilityType === 'specific_routes' ? (
                      <small style={{ color: '#2196f3' }}>
                        {card.assignedRoutes?.length || 0} route{card.assignedRoutes?.length !== 1 ? 's' : ''}
                      </small>
                    ) : (
                      <small style={{ color: '#4caf50' }}>All Passes</small>
                    )}
                  </td>
                  <td style={styles.td}>{card.mosaicOptions?.allowedSizes ? card.mosaicOptions.allowedSizes.join(', ') : 'All'}</td>
                  <td style={styles.td}>{card.showGallery && card.galleryImageUrls?.length > 0 ? `${card.galleryImageUrls.length} imgs` : <small><i>None</i></small>}</td>
                  <td style={styles.td}>{card.isHidden ? <span style={{ color: '#dc3545' }}>Hidden</span> : <span style={{ color: '#28a745' }}>Visible</span>}</td>
                  <td style={styles.td}>
                    <button onClick={() => handleEdit(card)} style={{...styles.actionButton, backgroundColor: '#17a2b8', color: 'white'}} disabled={isSubmitting}>Edit</button>
                    <button onClick={() => handleToggleHide(card.id, card.isHidden)} style={{...styles.actionButton, backgroundColor: card.isHidden ? '#28a745' : '#ffc107', color: card.isHidden ? 'white' : 'black'}} disabled={isSubmitting}>{card.isHidden ? 'Show' : 'Hide'}</button>
                    <button onClick={() => handleDeleteCard(card.id, card.imageUrl, card.galleryImageUrls)} style={{...styles.actionButton, backgroundColor: '#dc3545', color: 'white'}} disabled={isSubmitting}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminExploreCardsPage;